<template>
<div class="chapter">
<div class="inner_page">
<p class="ct"><a href="#/contents#ch2">
					CHAPTER II</a></p>
<p class="ct1"><a href="#/contents#ch2">
<i>Wisconsin to St. Petersburg</i></a></p>
<p class="tx">
<i>...The German cried“Then why the devil are you riding through this poisonous land
					against the Turks?” The marquis smiled. “In order to return.”</i> </p>
<p class="txr">
					Rilke’s <i>Cornet</i> </p>
<p class="tx" style="margin-top:2em;">
					I don’t know what we could have been thinking of. George Smalley, my senior colleague,
					learned <i>his</i> Russian in the military crash program at Syracuse. Then, he’d fallen
					in love with Prague School linguistics at the University of Chicago. My Russian came
					from the University of Madison, Wisconsin and from nine months in Moscow in ’61 on
					the government exchange program. We wanted, I guess, to plunge our 60’s students,
					theoretical radicals in large part, into the real grit of socialism, to guide them
					to the point from which they could “kill their own lion” as George, operatic as always,
					had put it in a much-quoted graduation speech. </p>
<p class="tx">
					I’m a comparative literature type, rather too prone to roleplaying in foreign cultures,
					sure that literature will somehow make sense of life. George, more scientific, drove
					and led our common students to develop a system of Russian grammar without irregular
					forms, a smoothly functioning set of rules to straighten Russian’s interminable meanders.
					Neither of us had time <span id="z31-1">for academic politics, both scorned mere “advancement.” Finally
					though, we were both mesmerized by a country, a culture, utterly foreign to our U.S.,
					Midwestern expectations. As Susan Richards wrote: </span></p>
<p class="tx">
<i>What was it that I should have known about Russia, about myself, before I embarked
					on this journey? I tried to explain what had been happening; how every day I seemed
					to understand less and less of what went on around me, how the ordinary transactions
					of life worked ... “Understand!” this mild-mannered man broke in, incensed at the
					word. “You are speaking like a Westerner! Logically! But life here is not logical!
					Understanding immobilizes! Once you understand the problem here you know that it
					is irremediable. It is better not to understand too much if you want to remain free
					to act.”</i> </p>
<p class="tx">
<i>There was nothing more to say.</i> </p>
<p class="txc">
					*** </p>
<p class="tx">
					We in the West believe in problem solving. Every day discovers a Thomas Edison or
					Henry Ford of maturation, marriage counseling, monetary success, sexual expertise,
					a love or a death guru. George and I were fascinated by the essentially innocent
					absurdity of Raskolnikov’s desire to be Napoleon, the irresolvable nature of Anna
					Karenina’s passion: by the rich stasis of nineteenth century Russian culture, where
					everything is hopeless, yet vital beyond the iron theories of Bentham, Darwin, or
					Malthus, certainly beyond the cheap absurdities of our own Norman Vincent Peale.
					</p>
<p class="tx">
					Repelled by America’s overwhelming prosperity, but hypocritical enough to enjoy it
					at times, we were attracted, as our students also have been, by the buoyancy, generosity,
					and sheer verve of this people whose virtues become <span id="z32-1">most evident in disaster. Then
					again George, largely English by descent, prided himself on some mysterious admixture
					of Gypsy blood, and a need to wander. I wanted the sheer pleasure of speaking Russian
					again; eight years having passed since my stint in Moscow on the government exchange.
					Perhaps it is the different mask or role that goes with the use of a foreign language
					that attracts me. A fierce opponent of “selfesteem” education, I have, nevertheless,
					little enough of that element in my everyday American existence. Whatever the case,
					whether the basic cause was curiosity about Russia, Gypsy blood or a desire for theater,
					one bright October day in 1967, drinking coffee at the student union, we dreamed
					up a camping tour of eastern (now central) Europe and Russia. George’s appetites
					were vast. He convinced me that we needed not a normal ten week term, but a full
					fifteen week summer, to “do,” which was, of course, suspiciously American, all eastern
					Europe, Russia and Istanbul as well, that source of Russian Orthodoxy, site of Hagia
					Sophia, Holy Wisdom, the mother church. </span></p>
<p class="tx">
					I don’t remember just how we sold Dean Francis Broderick on the junket, but I can
					guess. George, who had sold books, hawked paint and could have merchandised hourglasses
					in the Sahara, was certainly the effective source of our success. He had impenetrable,
					thorny eyebrows, absolutely Tartar cheekbones, and the certain, piercing glint of
					a seer or shaman. In George’s urgent presence, the Dean might have been convinced
					to buy a moonwalk. Dr. Broderick knew, too, that George spent fifty hours a week
					haranguing his linguistic troops and had never paid any attention to the officially
					prescribed six-hour-a-week teaching requirement. Whatever program he touted, George
					<span id="z33-1">would most certainly do more than his part. As it turned out, we easily got the go-ahead
					from the president and faculty to sleep in slavic swamps for the summer of ’69. </span></p>
<p class="tx">
					Alexander Lipson of MIT was, at that time, running summer tours of the Soviet Union
					for high school students. Another Prague School adept, he agreed to schedule and
					equip a tour for us as well. He came to Lawrence to explain his touring procedures,
					lectured and demonstrated traveling light in the East. One shirt, two pair of socks,
					Woolite, dried fish from peasant markets, all personal possessions to fit in one
					small suitcase: 15” x 28” x 4”. We would travel and live in used VW vans carrying
					tents, sleeping bags, propane burners, and kitchen equipment. We rode seven students
					to a van, leaving room for the obligatory two Intourist guides. Each bus kept a daily
					account of purchases. A mechanic’s van with spare parts and a bookkeeper completed
					our six-machine fleet. </p>
<p class="tx">
					We got right off the plane from Chicago and into these buses at the Brussels airport,
					and then dropped the vehicles back in Brussels fifteen weeks and fifteen thousand
					miles later. Every day we rotated as navigators and drivers, teaching the intricacies
					of the stick shift to the uninitiated. Our students blossomed in this hard, unaccustomed
					school, certainly because Lipson had spent a good few years perfecting his system.
					When we began our trip, in 1969, it was possible to set the same price for a summer
					in eastern Europe, as Lawrence charged for an eleven week term, and to offer the same
					three credits, one having been earned in advance in a special linguistics course
					for the languages other than Russian that we would meet. The other <span id="z34-1">two credits were
					for conversational Russian and a trip journal. The students themselves researched
					and produced travel and language guides. </span></p>
<p class="tx">
					Our itinerary ran more or less as follows: Brussels, Finland, the Soviet Union; Leningrad
					to Odessa to Kiev; Poland, Czechoslovakia, Austria, Hungary, Rumania, Bulgaria, Turkey,
					Yugoslavia, Brussels; a great, meandering loop through Eastern and Central Europe.
					George, leader and autocrat, s<i>amoderzhets,</i> was urban and hasty. I, an avoider, too
					seldom willing to quarrel, preferred rural and slow, but kept up as best I could.
					The pace was exhilarating. </p>
<p class="tx">
					We did this tour, with minor or major changes, twelve times between 1969 and 1991.
					Cost increases, easier opportunities to visit and study in the Soviet Union, the
					breakup of the bloc, then of the Union, a quite new prevalence of crime in the East,
					our own aging, all contributed to the trip’s demise. We had had a good, long run.
					</p>
<p class="tx">
					The incidents that follow, largely sketches from my trip journals, will follow our
					itinerary as much as possible. The account then concludes with a Krasnodar semester
					in 1991, an alumni tour on the Upper Volga and another Krasnodar semester in 1997.
					Its connecting theme: what the Russians had to teach us about living. Our summers
					in the East made the Slavic trip the beginning of a lifelong passion for Eastern
					European survival culture for George, for me, and by their own testimony, for a majority
					of our 300-plus student traveling companions. This book is for them, in fulfillment
					of a promise to sometime write a work entitled <i>The Cheap Cafes of Eastern Europe</i>
					which Matt, one of our students, prophesied and proposed years ago. </p>
<p class="txc">
					*** </p>
<p class="tx">
					“But you <i>do</i> know,” an East German teacher once said to me, “which side you are on.”
					As I seemed to be at the midpoint of what Hemingway called the classical swing from
					left to right, I didn’t honestly have a side to be on. Nevertheless, because Herr
					Doktor Bauer needed to believe in the existence of conviction <i>somewhere,</i> I hypocritically
					agreed that the way of the “free world” was best. Though this was fifty years ago,
					I find that I still have not finished with the question. </p>
<p class="tx">
					Seven times, once every four years beginning in 1969, I took part in Lawrence University’s
					Slavic Trip. This served to keep my Russian somewhat current, but its greatest value
					was that it removed me from my usual summer rut. Instead of exchanging tractoring
					for beef on a dairy farm, I was brought cheek to jowl with my students. All about
					us lay my subject matter: Russia. On days when we switched camps, I sat in a VW bus
					for eight or twelve hours a day with six of my students. Then, for a day or two,
					I lived in a Russian campground or motel, and spent the days “doing” Leningrad, Turgenev’s
					estate, or a Black Sea beach, when the adolescent desire to roast on rocks became
					strong among the troops. In rain or baking weather, there was no good place to read
					a book. There were amazingly few administrative matters to deal with. I had somehow
					to endure the bottomline realities of my students and myself in farthest Slavia.
					I was not always pleased with the students or myself. They once even voted me out
					of one of their buses. Russia and the students did teach me, although I’m not a quick
					study. </p>
<p class="tx">
					My colleague and eternal enemy, George Smalley, seemed to love and <span id="z36-1">enjoy students.
					I found them, in certain moods, amazingly ignorant and, worse, autistically self-immersed.
					My Marxist youth saw them, largely bourgeois and suburbanite, as the products of
					a wealthy, wasteful, plastic and, necessarily, a declining culture. Nine months of
					Russian life in 1961, however, had given my Marxist youth a severe battering. Class
					judgments never came quite so easily after my dissertation stint in Moscow, though
					they did, and do, still come. I will try, here, to show how the students and Russia
					nudged me toward other kinds of judgments, albeit not final ones. Which side <i>am</i> I
					on? </span></p>
<p class="txc">
					*** </p>
<p class="tx">
					We traditionally left Appleton on the morning of graduation. Once George even interrupted
					that holiest of academic ceremonies to gather the troops and leave early as we hadn’t
					been able to arrange a later flight. Although one may, certainly, discuss the nature
					of true education, George was forever more than ready to let our colleagues and administrators
					feel that only “out there,” in Russian reality, could anything really be learned.
					Not everyone took this in good part. Luckily, our critics couldn’t know that trip
					preparations, given the approach of graduation and the ritual sufferings of final
					exams, were usually incomplete. We were always more or less unready. </p>
<p class="tx">
					In Chicago, George left me to charm parents arriving early to see their sons and
					daughters off. He disappeared into an O’Hare pub for a couple of manhattans. A Chicago
					friend, Frank, my college roommate, helped me. With the aid of parents, slivovitz,
					and then a mixture of the two, we transferred cases of peanut butter and ketchup
					from parental cars to unbelieving Sabena <span id="z37-1">freight handlers. The slivovitz somehow
					cancelled out the offensive peanut butter. The parents were excited about adventures
					ahead for their offspring and, for a bit, wished that they could go along. They trusted
					me and the now-returned George in a child-like way, perhaps because we spoke with
					automatic Russian accents and weary authority, both taken from old movies. Three
					missing passports magically appeared at the final boarding gate, and we were off.
					</span></p>
<p class="tx">
					George and I, depression kids, never quite believed that we were on our way to Europe.
					The students, who have been flying since birth, were more blasé and snoozed. We couldn’t
					sleep, so we flirted all night with the Polishborn flight attendant. She brought
					us extra dinners and cognac, gratis, and we imagined ourselves to be cunning eastern
					European operators. We ostentatiously refused to watch the American film. </p>
<p class="tx">
					When our jet met the dawn, our Polish cutie had seventy five passengers to serve while
					five flight attendants served thirty businessmen in business class. She was armed
					with blue, shiny, aluminum make-up, small eyes, one squinting, and her anger. The
					sound for the film hadn’t functioned, the john was super-tiny and not very clean,
					the food less than so-so. Jet steerage? A white-haired man demanded and got a real
					flute for his champagne. Who am I to complain about <i>any</i> trip to Europe, son of a
					good man though a failed chiropractor, grandson of a drunken, loving plumber? Any
					flight will do just fine. </p>
<p class="tx">
					Brussels at noon. Our advance crew, led by Greg, had the VW buses packed with tents,
					camp stoves, dried soup, French bread, and Edam cheese. <span id="z38-1">We bulled through a jet-lag-dogged
					drive to Holland and our tents were initiated that first afternoon behind a dike.
					I rose earlier than the others to the sound of a cuckoo, dropped my tent, took a
					morning shower, and enjoyed a brisk walk. Under way, old woodsman that I am, I realized
					that the sun was sinking in the west, not rising in the east, and that I had utterly
					lost track of time and direction. I rolled my sleeping bag under the bus and went
					back to sleep. The students woke me when our first genuine European morning arrived.
					Mistakes of time and direction abound in me, it seems. </span></p>
<p class="tx">
					We visited Bruges first, and then Amsterdam. We’d hardly heard of Bruges, but knew
					it meant neat bridges, which it certainly did. It was a tight little town, preserved
					with much effort, but preserved! Damn the automobile! There was a quiet, mossy-treed
					park with Flemish and Latin tags on the trees, Queen Astrid’s Park, a subtle jewel.
					</p>
<p class="tx">
					The students on my bus were Stephanie and solid, gentle, Cindy, afraid of me from
					her sister’s tales out of class. She asked “stupid,” good questions like “Why brick
					houses? Why pointed roofs? What do Belgians <i>do</i>?” Then there was Jane, quiet and not
					used to smiling, plugged into her Walkman, scared? Steve was large and generous,
					smiling, and Kevin, a good, active, unshaven sort. Dawn, a genius at Russian, was
					more open with others than I would have imagined, kept careful books, and read maps
					most exactly. Good! Good bunch, it seemed. George passed us rapidly in <i>his</i> bus: how
					jealous of sovereignty, forever a child, I guess. Who <i>cares</i> who’s boss? (Did <i>I</i>, sometimes?)
					</p>
<p class="tx">
					We cruised the flat, small fields of Belgium and Holland. There were one <span id="z39-1">or two people
					cultivating potatoes and cabbages, by hand, in utterly level, closely hedged acres.
					Two thirds of the landscape is sky, as in Ruysdael. Two men and a dog were walking
					out to their barge in low water, rather far inland but still tidal. Hip boots. Everything
					ditched and drained. This hay - Sudan grass? Why were the cows so clean? One saw
					a few more horses than at home and small flocks of sheep, subsistence farming still,
					which seems the only real life. </span></p>
<p class="tx">
					That evening in Amsterdam, we were involved in a long search for the island campground
					somewhere near the middle of the city. This town was like an honorable, bourgeois
					house with angry, disorderly children. Graffiti on a water tower, writ large: “Kill
					the Poor!” Tired, hungry, we roared with laughter. Was it funny in its utter outrageousness?
					(We hardly picked up on the irony.) </p>
<p class="tx">
					Having found the campground, which was full of crowing pheasants, we got a lecture
					on the evils of marijuana as the camp director absentmindedly rolled himself a joint,
					then directed the kids to “The Bulldog” tavern where, this season, the action was
					said to be. They returned, bored, early enough to die laughing as they put up tents
					in a rain so heavy that there was, clearly, no longer a reason to put up tents. Super
					nice, that laughter. May we always meet discomfort so. </p>
<p class="tx">
					Lovely in their endurance, the students do always strike me as amazingly young. That
					their age remains constant, while mine doesn’t, must certainly be one of the problems.
					As I got beyond their names and obvious features, I found that I was accompanied
					by a vegetarian couple who set the eating tone <span id="z40-1">for the rest, and a born again slick
					operator who, quite unjustly, was letter perfect at the guitar and the Russian language:
					an artist and a poet. They were all, naturally, Americans. I am a hardened chameleon.
					Because I supposed myself to have learned from bad experience: the bad experience
					of students with me, I decided not to drive the bus unless absolutely necessary.
					I would let these students discover Europe without the aid of my vast expertise.
					However, if I am not teaching and leading, I don’t know what to do with myself among
					students. I tried to listen. They seemed amazingly young. </span></p>
<p class="tx">
					I always forget that they are, in public at least, pack or peer group animals, and
					democratic to boot. When they sing, they sing sad and self pitying songs with lyrics
					like “I am a Rock, I am an Island.” This is irritating. Why don’t they sing Russian
					songs? It may be because they don’t know any. Mainly though, because “good” music
					and “good” books and “good” conversations are classroom demanded things. At their
					age, they need to rebel against what an older and, from the look of the world, not
					very wise generation demands. They need to belong to their own generation. Still,
					until I learn better, I act as if they must be, maybe socratically, guided to my
					truth, my good. The bus was not very comfortable. </p>
<p class="tx">
					It was physically uncomfortable. Seven people, with occasional guests and tour guides,
					had to live in that tin box for fourteen weeks. Luggage, much too much, food, utensils,
					spare tires, an extra windshield; we needed order. I tried not to insist, but became
					enraged. My controlled bile at odd socks coated with peanut butter, loose pages of
					travel guides and dirty dishes, was certainly worse than open anger. I traded the
					role of voluble pedagogue for <span id="z41-1">that of a silent, implacable judge. All this took about
					two days. </span></p>
<p class="tx">
					By now, we were passing Hamburg where the kids were generous enough to drop me at
					a friend’s house, although my bad directions cost them half a day’s travel time.
					Dieter and Frieda gave them coffee and pastry, an insider’s view of what to see up
					north in brick-Gothic Lubeck, and maps from Dieter’s insanely large collection. I
					would catch them in three days in Sweden. Teacher and students were relieved at the
					break, I think. </p>
<p class="tx">
					Hamburg took me, in time, back to my own youth, as a Fulbright exchange teacher and
					student. My friend Dieter, five years older than I, remarked on the wholesome, intelligent,
					brisk quality of my students. His children, one rebel in particular who is my godson,
					are managing to be both banal and corrupt, irresponsible, messy, socialistic and
					what? <i>Young.</i> I took the part of Gerd and Reinhardt, defended the “greens” and the
					antiwar movement, <i>understood</i> his children though I was somehow incapable of forgiving
					my students their youth, and got into one hell of an all night argument with Dieter,
					their father, my old friend. Himself a Fulbright scholar and erstwhile noisy heckler
					of Joe McCarthy, he regarded the present antiwar meeting, which was tying up downtown
					Hamburg, as of a piece with the bad manners, dress, thought, and culture of his children.
					Reinhardt, on the upstairs piano, tactfully played Beatle songs and Beethoven by
					turns. If he was fiddling while his father burned, he was doing it with ability and
					tact. Dieter, unhearing, blamed me for his sons’ politics, music, and nonchoice of
					careers. Would I ever grow up? I was doubtful and not very repentant either. </p>
<p class="tx">
					As George was with our bus cavalcade, I took three days in Hamburg. <span id="z42-1">Long, spacious
					flatland walks, Westphalian ham, the return of grammatical German, all placed me,
					also <i>vis-à-vis</i> Dieter, back at the age of twenty two. Then this experienced European
					tried to talk sense to a raw Wisconsin romantic. I was sobered, but hardly reformed,
					when Dieter and Frieda, according to that lovely continental custom, warmly saw me
					off on the overnight train to Sweden. </span></p>
<p class="tx">
					In Linkøping, Sweden, where I caught the troops, Øke and Margaret Anderson, complete
					strangers, had heartily taken in a busload of us because they had relatives in Chicago.
					Good, generous people, they included me, too. There turned out to be a dance nearby
					on Saturday night, so we went, the students taking the “family” car. The Andersons
					and I arrived later, after an elderly bout of Norwegian herring and Finnish vodka.
					My morning antiAmerican feelings, about littering, a rarity here, were balanced by
					evening second thoughts as Margaret, who speaks not English but German, described
					the difficulties of <i>Swedish</i> life: no litter, no advertising on the roads, but many
					social problems. They, as responsible officials, Øke a manager who rents out apartments,
					Margaret a clerk for the Nykøping police, dared not be seen drinking or dancing anywhere
					in their home town. So even Swedish <i>officials</i> spent their Saturday where they weren’t
					known. What unfreedom! </p>
<p class="tx">
					The dance was a great success although Margaret was interested in learning the Twist
					from <i>me</i>. The students all found Swedish partners, changed every two dances, met a
					tipsy Finn whose one phrase was “I love Americans” (<i>girls</i>, he meant, and they really
					are called <i>flicka</i> there) and felt, I think, accepted for once; successful evening,
					though Øke did have a hard <span id="z43-1">time bringing himself to ask Jeannie Knight to dance.
					All Swedes seem to consider Americans born dancers and Øke was shy. Jeannie finally
					just grabbed him, and the dancing went fine. </span></p>
<p class="tx">
					The Scandinavian countryside is not unlike the Fox Valley around Appleton, but because
					it is north European, and I first saw it at twenty two, I observe it with a more kindly
					eye than I usually spare for Outagamie county. How snobbish I can be! In the Stockholm
					campground, reunited with <i>my</i> bus, I taught the students, at their request, a couple
					of Russian songs: <i>“Rassvetali yabloni i grushi</i>.” Peace or, at least, a truce. They
					had camped in a refuse dump on the way to Sweden and were filled with the very real
					triumph of having done everything wrong and survived. </p>
<p class="tx">
					I woke up between five and six when the sun hit me. I was on a rocky cliff above
					the fjord that morning; the sky was blue, the Swedish ships passing were clean and
					white, and Walt Drymalski, with whom I had argued and agreed and drunk beer until
					late into the night, had left his rod and reel beside my sleeping bag for me to use.
					I did. The rainbow trout are prism bright in that clear water. </p>
<p class="tx">
					Last evening we found a place on this cliff where there were enough pine needles
					to insert tent pegs and in that way managed to avoid the crowd in the main camp.
					Since it was a holiday night, almost Midsummer’s Eve, too much beer was drunk by
					all, and a Swedish policeman together with an Alsatian police dog appeared about
					midnight to ask for quiet. The students, who had been talking, among other things,
					of revolution, immediately pointed to me. “Der Herr Professor Doktor Yatzeck.” (I
					had taught them that as <span id="z44-1">a joke, of course, but they were smart enough to use it.)
					I, who was mere minutes before a convicted representative of the “Fascist Establishment,”
					was nevertheless expected to retrieve the situation. The policeman, very polite to
					a “Professor,” happily watched the students disappear into their tents when I called
					out “Bed time!” </span></p>
<p class="tx">
					After a lethargic start, we had a good and short drive to the ferry at Nortalje.
					All six buses arrived, drove on the ferry, and fell into an all-night bull session,
					a good one. We met two Finns, one a Social Democrat, one a Communist, who knew, between
					them, some Russian, English, and German. With four of the students we talked politics
					and the possibility of loyalty to anything larger than one’s home village ‘til 3:00
					a.m. Now and then, in that white night, we passed tiny islands, often sheltering
					a single cabin and a boat. They looked like the very essence of “home,” self contained
					and self sufficient, just the right size for loyalty. (That must have been between
					Åland and Turku.) </p>
<p class="tx">
					We nocturnal politicians slept through the arrival in Turku and the drive to the
					Finnish capital. Helsinki was still European, rather than Californian, and relatively
					shabby. Machine gun scars from the 1940 Winter War with the Russians were sharply
					visible. I noticed something I had not seen for a long time, and certainly not in
					contemporary Hamburg or Stockholm: the womanliness of a certain kind of European
					woman - often poorly dressed, but always with style - sexy, but not in the American,
					striven-for manner. These Finnish ladies were conscious of themselves, glad to be
					admired, but not bothering much about it. I don’t know if I can explain this well.
					The <span id="z45-1">main ingredient is not sex, but self-possession, not <i>trying</i> to be anything, and
					it conveys the electricity that Pasternak speaks of when describing Lara: beautiful
					in <i>herself</i>. </span></p>
<p class="tx">
					We met the Gypsies in Helsinki. They were on their way to an annual Gypsy summer
					gathering, though we never found out where. Gypsies are always on their way. We were,
					by contrast, utterly amateur voyagers and thus, from their point of view, helpless
					prey. One of our cohorts, Frank Schafer, somehow offended a Gypsy grandmother and
					brought down a curse on his van which was, unfortunately, also colleague George’s
					van. There was, throughout the following six weeks of Russian roads, hardly a day
					when the “Gray Goose” didn’t lose a wind shield to Russian road gravel or simply wobbled
					to a helpless, unplanned halt. Flat tires were constant. We were often forced to
					divide the van’s occupants and carry them on other vans while towing the helpless
					Goose. This meant that the Goose’s inhabitants had to travel without their luggage
					or kitchen supplies, like peanut butter. We did, though glumly enough, share with
					them. Finally, the Goose was safely dragged to Kiev, where a thirsty master machinist
					spent a rainy Sunday morning turning a new piston for the price of a bottle of vodka.
					On this first cursed yet blessed trip, it also rained eighty-five days out of one
					hundred and I, thirty-six and still married, fell in love. (Frank Schafer, wherever
					he is, has much to answer for.) </p>
<p class="tx">
					To run far ahead of my tale for a moment, the new piston got us halfway to Odessa.
					We dragged the Goose empty the rest of the way and shipped her, with George and a
					full complement of students, to Istanbul and the nearest <span id="z46-1">VW garage. Our resident
					mechanic, having sold his passport, jumped aboard that same ship and hid. I don’t
					know how he entered Turkey, but we were left without a repairman. All of this, all
					of it, Frank, because you stopped singing “Hey, Jude” just long enough to badmouth
					that Gypsy grandmother in Helsinki. </span></p>
<p class="tx">
					Further, befriended by cute, Gypsy children, our mellowest van lost its clothes,
					cash, and even an iron reserve of traveler’s checks. Again, I personally dragged
					three Gypsy boys out of our van, and emptied their pockets of Walkmans and tapes,
					though I do hate Walkmans. I believe, finally, that the imploring Gypsy mothers at
					every Moscow stoplight were carrying large, <i>rented</i> babies. Our commerce, then, with
					Gypsies was, well, confrontational. Still, their bright clothing in a dreary world,
					the sound of their flutes and violins on damp Russian evenings, their supreme ability
					to live off the land; in sum, their ancient nomadic existence, like that of the Mongol-Tatars
					say, has also, like the fearsome attraction of wolves, a dangerous magnetism for
					something in my own nature. Bruce Chatwin quotes a Mongolian poem: </p>
<p class="txc">
<i>You have a house and a mill.</i> </p>
<p class="txc">
<i>I have a horse and a whip.</i> </p>
<p class="txc">
<i>I will kill you, and go.</i> </p>
<p class="tx">
					I find these lines, at odd moments, weirdly attractive, in Russian, <i>zhutko khorosho.</i>
</p>
<p class="tx">
					After Helsinki and its Gypsy grandmother, we rolled on to Hamina, the last Finnish
					town before the Soviet border. Hamina campground offers a rough, sprawling log sauna
					on the Gulf of Finland and the last chance to store <span id="z47-1">away Western delicacies: hams,
					oranges, even toilet paper. After shopping and a sauna, we risked Ultimate Frisbee
					among very sharp, black rocks out in that Gulf. </span></p>
<p class="tx">
					There had been little advertising along the roads, the countryside not yet swinish
					with beer cans, and, among the Finns, an ironic and quiet view of socialism <i>and</i> capitalism.
					They killed the Russians five for one in 1940, on skis and with knives (<i>finnkas)</i>,
					surrendered only when the Red Army took Helsinki, and were very much on their own
					side. </p>
<p class="tx">
					The Hamina town square was our rallying point for the invasion of Russia. Someone
					brought a cardboard marshal’s baton for George, photographs were taken of the still
					neat platoon of VW buses, George shouted a John Wayne “Ho-o-o!” and off we charged.
					Steve, who had rather copiously celebrated his last night in freedom, dressed for
					this crucial border crossing by finally standing upright in his tent, thus ripping
					tent and wrenching out tent pegs, belting this outfit with the laundry line, including
					laundry, and then rolled back into the food storage shelf of our bus. Steve was the
					ultimate of cool: his Russian grammar was nonexistent and yet he became, nevertheless,
					one of the best of our speakers on the coming Russian roads, for his ears were good,
					as was his memory. Best of all, he could ignore, or didn’t feel, the stress of new
					encounters. </p>
<p class="txc">
					*** </p>
<p class="tx">
<i>“We are inclined to accept the spirit as readily as the flesh.”</i> </p>
<p class="txr">
					Andrey Sinyavsky, <i>A Voice from the Chorus</i> </p>
<p class="txc">
					*** </p>
<p class="tx">
					Whatever real dangers may have been present, the actual crossing of the Soviet/Finnish
					border provoked an almost superstitious fear. This was, no doubt, due in part to
					the rhetoric of the Cold War, but the season played its part as well. We always crossed
					a day or two after the Scandinavian celebration of Midsummer’s Day, the sky barely
					darkened at night, and our heads were full of Dostoevsky’s <i>White Nights</i> and <i>Crime
					and Punishment</i>. Suburbanites all, we wanted the ominous, we wanted to be frightened.
					The Soviet border troops generally obliged. </p>
<p class="tx">
					Fifteen, or perhaps fifty kilometers out of Hamina, the physical distance isn’t the
					point, on a gravel road surrounded by Finnish mist and spiky conifers, we passed
					gray-clad Finnish troops. Then, in a black-magical fifty yards, we spotted a khaki-clad
					Russian with a tommygun, a green band around his service cap, and a fierce-looking
					guard dog. This apparition checked the lead van’s papers, raised the red-and-white
					painted wooden bar - like a railroad crossing barrier - saluted, and beckoned us
					past. We climbed, then, along rutted stone and clay to the top of a low, bald hill
					and pulled up beside a ramshackle wooden barracks with guard kiosks in front and
					a row of what looked like oil pits. These were for inspecting the undersides of vehicles.
					An officer and a couple of slovenly privates collected our passports, the papers
					for all of the vans, ordered us to get out and requested that we utterly unload.
					As traffic is light here, the border guards inspected every tent, sleeping bag, with
					a cold-eyed roughness just short of sadism. English language books sporting the hammer
					and sickle on the cover were confiscated. Those with the barber-pole cathedral of
					Vassily the Blessed or with beaming Russian <span id="z49-1">grandmothers were given back. None of
					the guards seemed competent in English. The search procedure took four hours or so.
					Actually, we enjoyed it. We enjoyed our mild fix of Soviet brutality. </span></p>
<p class="tx">
					Our leader, George, having served in military intelligence, half expected to be turned
					back, expelled. He kissed the Tolstoyan earth when it appeared that the insidious,
					all-knowing Soviets didn’t have a file on him. As a first installment of the Gypsy
					curse, George’s bus had a flat tire right at the border. A short, stocky Russian
					civilian simply picked up the back corner of the loaded vehicle when we couldn’t
					immediately find the jack to change the tire. He accepted no reward, and said “My
					pleasure, friends,” and then evaporated. </p>
<p class="tx">
					There were several hairy moments. First, a guard with a hand-held metal detector
					picked up the knot of gold chain that I was smuggling in for friends. As precious
					metals are contraband, I could have been turned back, even arrested, but as I sweated
					a big drop, the guard simply said “loose change” and passed me without checking my
					pockets. Then, a Dutch couple applied to us for translation help. Driving a propane-fueled
					car with a large gas cylinder in the trunk, they hurriedly explained that the guards
					were about to penetrate this odd tank with a blowtorch to see what it contained.
					Our shouts of “PROPAN! PROPAN!” finally got through to the blowtorch operator and
					just averted an explosive border incident. </p>
<p class="tx">
					Finally, Wesley, the born-again wheeler-dealer, was attempting to slide through with
					a dozen Bibles. As the search of our bus intensified, it developed that he had also
					hidden a <i>Playboy</i> magazine under <i>my</i> seat, thinking it might be saleable but knowing
					it was forbidden, like the three pairs of jeans that he <span id="z50-1">was wearing to peddle in
					Leningrad. (When I asked Wesley about born-again Christianity and greed, he explained
					that whatever made him happy also pleased Jesus.) In any event, the Bibles and the
					<i>Playboy</i> added a good hour to our halt at the border. The best of the adventure was
					the Solomonic judgment of the woman officer in charge of searching us. Her unusual
					neatness and dutiful air made us tremble. She was patently a very straight <i>komsomolka</i>
					in good Party standing. Still, she twinkled a bit as she pronounced: </span></p>
<p class="tx">
					“We take eleven Bible, and <i>Playboy</i>. You, Vesley, take one Bible. You need.” There
					was also a good deal of bass giggling when the forbidden magazine unaccountably appeared
					in the guard kiosk, where the hams and sausages confiscated as carriers of foot and
					mouth disease were being consumed. Still, except for Wesley, we went our way, finally,
					rejoicing. </p>
<p class="tx">
					A bungled Bible shipment fits here because our student trip to Russia was in the
					nature of a pilgrimage. We were, of course, not on our way to the Holy Sepulcher,
					but to socialist “stations of the Cross,” so to speak. Lacking, as Russian governments
					have always lacked, material benefits to shower upon a grateful people, Marxist ideology
					simply took the place of traditional religion here as the “opiate of the masses.”
					</p>
<p class="tx">
					Beginning at the border, in Vyborg, Leningrad, Novgorod, and Moscow, seemingly every
					flat surface bore white-lettered red banners proclaiming that “Lenin lived, lives,
					will live,” or that “Lenin is with us!” His relics in the red and black marble tomb
					in Red Square liken him to the Orthodox saints and monks on view in the Cave Monastery
					in Kiev. The omnipresent <i>znachky,</i> tiny lapel pins, portrayed Lenin, Stalin, and lesser
					notables of socialism in <span id="z51-1">very much the manner of religious medals. As Sinyavsky wrote,
					Russians do “accept the spirit as readily as the flesh.” They have never developed
					the habit of plenty. Wesley’s Bibles would have fallen on fertile ground, and probably
					did, for the badly paid border guards most likely merchandized them. On the other
					hand, the young woman in charge may equally, and <i>religiously</i>, have burned them. </span></p>
<p class="tx">
					Beyond the border, there was nothing for fifty kilometers but a bad, sad road with
					conifers, and occasional kids shouting, begging “kau-gummi,” the German for chewing
					gum. There was, indeed, until the later ‘80s, no road as good as an average Wisconsin
					two-lane, city boulevards excepted. Russians and their goods move, still, mostly
					by train. Only beyond Vyborg did we see towns and villages, and they reminded me
					of Walker Evans’s photographs of the Depression South. Finland and the U.S. were
					in another century, except, say, for the Bronx. Which side am I on? </p>
<p class="tx">
					The students earnestly practiced Russian with the Intourist guide who joined us in
					Vyborg, when I had the sense to let them get a word in edgewise. The physical poverty
					of the country was so striking that they did not make comparisons. Although some
					guides had a certain stock set of propaganda speeches, one - Raya - more practically
					explained the necessity of making pit stops in the woods. Socialist toilets were
					really too much for green foreigners. Indeed, all public conveniences, trams, say,
					or elevators, were, and still are, messy, and tend to work more or less spasmodically,
					except for the Moscow subway, still <i>the</i> parade piece, far superior to London’s neglected
					tube, still safer than New York’s. This paragon, however, like the White Sea Canal,
					was <span id="z52-1">mostly built with the labor of political prisoners in Stalin’s time. </span></p>
<p class="tx">
					Just outside St. Petersburg, we found our motel, built for the Olympics, already
					visibly cracking and decomposing. Thrown up to meet a deadline, the cement was poured
					in zero degree weather. An all-night bar featured the latest pirated rock music and
					New York prices. The Russians who frequented it, reminiscent of the well-connected
					<i>stilyagi</i> of ‘61, were much better dressed than we were. They certainly didn’t ask
					to buy our Levis. <i>They</i> wore designer jeans. </p>
<p class="tx">
					At 10:00 a.m. a Russian charwoman, one of the millions of such earnest <i>babushki,</i>
					was trying to get a literally stiff young man to bed before the officious doorman
					could have him hauled off to the “sobering up station.” She was not his grandmother,
					but said, as I took his feet, and she his shoulders, that she was responsible for
					him. I asked why. She said, “For Christ’s sake,” with the intonation of an answer,
					not an exclamation. Russia is Dostoevsky country still. </p>
<p class="tx">
					Later that same day, we visited an Orthodox service, and another <i>babushka</i> approached
					me to ask that I “organize” monetary help for a blind woman seated at the entrance.
					Embarrassed, I had to admit that neither I, nor my students, had been able to exchange
					money yet. “It doesn’t matter, <i>nichevo</i>, said the woman, so bent herself with arthritis
					that she was forced to look along a raised shoulder to regard me from below. “Just
					pat her a bit, then.” For Christ’s sake, I guessed. I wanted to be on that woman’s
					side, but I found it hard to “pat” her protégée. I was, I guess, far too Anglo-Saxon,
					but I had been given a piece of the Russian puzzle. </p>
<p class="tx">
					The week in Leningrad did not continue this well. The Intourist guides insisted on
					World War II memorials and all-day tours of the city. This kept us grouped as “the
					Americans” and denied us conversation with old Russians sitting in parks. Leslie,
					the vegetarian girl, refused to continue a discussion about Shakespeare at lunch
					because her peers would find this pretentious. The vegetarian boy, Eric, and I hunted
					for an ice cream cone, following a trail of happy lickers, but the ice cream cart,
					when we found it, was sold out. </p>
<p class="tx">
					However, as evening arrived, Ann, the artist, had met a couple of sailors upon whom
					to sharpen her Russian (and they <i>were</i> handsome sailors) leaning against the edge
					of a fountain from the time of Catherine. Later, we made it into “Sadko,” a restaurant
					with the motif of a Novgorodian folk tale, where a quiet, preppy-looking Russian
					interrupted our efforts at square dancing to the balalaika to teach us, quite without
					words, a slow, elegant circle dance. It’s the kind that the “Beryozka” troupe does
					at Carnegie Hall, which gives the impression that the dancers are all on tiny, brass
					wheels. The evening was good - indeed, the reverse of the Russian proverb - for it
					was “wiser than morning.” </p>
<p class="tx">
					The first stage of our Russian experience always involved a war with Intourist. The
					guides assigned us were supposed to keep us grouped and to fill our days with what
					they seemed to consider progress and we saw as propaganda. Our innate chaos rather
					quickly wore down the guides. As soon as we were away from their home turf, generally
					Leningrad or Moscow, they “waved their hands,” <i>makhnuli rukami</i>, at our lack of order,
					and let their own native anarchism relish what their socialist discipline could not
					control. This <span id="z54-1">was both good and bad. We were free to do as we liked, but we missed
					real Russian treasures: a fine museum of the Kursk Bulge, the beginning of the ultimate
					Soviet counter-offensive against the Germans, or the painter Repin’s dacha near Leningrad,
					because the guides soon began to think that we would never be interested or punctual.
					Without a fixed class schedule and with repeated disappointments, as when once Tolstoy’s
					estata, Yasnaya Polyana, was inexplicably closed for repairs, the students devoted
					themselves to bare physical survival: more sleep, or keeping out of the heat or rain,
					and found it hard to be “up” for a monastery or a monument. There was always, near
					the surface, the feeling that monasteries and monuments were academic, i.e., not
					real. Still, the problem was a mixture of American attitudes and Russian difficulties.
					Isn’t that exactly the problem with any diplomacy? </span></p>
<p class="tx">
					Leningrad, christened St. Petersburg, has also been called Petrograd, when the German
					<i>burg</i> was too reminiscent of the Teutonic enemy. The city was renamed Leningrad when
					Lenin assumed Peter’s mantle. The inhabitants, indifferent to or, perhaps, flouting
					high politics, generally call their town “Peter,” far from politically feverish Moscow,
					which became the capital again under Lenin and the Soviets, the citizens have tended
					to be an independent lot. It was no accident that Stalin had Kirov, the Leningrad
					Party Secretary, murdered, and then began the terrible purges of the thirties from
					this spot. In 1961, after a revaluation of the ruble, I read the following on a sign
					affixed to an old woman’s newspaper kiosk: “We neither understand nor accept the
					new money.” In 1990, with most of the taxi drivers charging foreigners ten dollars
					and a pack of Marlboros for any ride, at least one old socialist we met <span id="z55-1">charged the
					usual price in rubles - about a dollar - for the same ride. Then, finding us to be
					Americans, stopped to buy us a doll in peasant dress as a souvenir. Cantankerous
					folks, and willing to lose by it! </span></p>
<p class="tx">
					Why do I come here? For the melodious phrase of an old woman at the well near the
					artist Repin’s dacha: “<i>khoroshaya voditsa,”</i> “good water,” or the latest slang: <i>“On
					ni boom-boom,”</i> “He hasn’t a clue.” For the generosity of Raya and Boris who share
					their boiled potatoes, wisely prepared in advance, with us when we run out of bread.
					For the glimmers of recognition among the students when they first see “The Bronze
					Horseman” and remember Pushkin’s poem. For the moments when I can be as feckless
					as they seem and delight in a ridiculous sign beside the road to Leningrad: “<i>Berigitye
					muravyei</i>!,” “Preserve the ants!” Boris, grinning, explained that this was not a cheerful
					Soviet acceptance of Dostoevsky’s definition of socialism as an anthill. It was,
					rather, the mark of a budding interest in ecology, for ants aerate the soil. </p>
<p class="tx">
					Whatever its official title, this “northern Palmyra” continues to strike the classical
					note of that Middle Eastern namesake. For me, the city’s heart is the Winter Palace
					on Palace Square, which, with its wing the Hermitage, houses Russia’s largest collection
					of Western art. </p>
<p class="tx">
					Rembrandt, for instance, can almost be measured by the acre. Like the city’s original
					suffix “burg,” the largely Italianate official architecture, and the Venetian canals,
					the museum is an example of Russia’s Western face, of the excruciating and often
					bitterly divisive attempt to be European. Of course, this was Peter’s intention,
					for he felt that Russia could survive only <span id="z56-1">on European terms. Still, even this massive
					monument to Western genius harbors the contradictions of Peter’s reforms. </span></p>
<p class="tx">
					These contradictions became apparent as soon as we arrived, tickets in hand, at the
					edge of Palace Square, the imperial setting of the Hermitage. The palace, with the
					square behind it, built directly on the Neva River, is pure eighteenth century in
					form, but both palace and square are so vast that they echo, almost, the steppes.
					One remembers that Peter was seven feet tall, and the empire came to cover a sixth
					of the earth’s land surface. Alexander the First’s column in the center of the square,
					built to commemorate the victory over Napoleon, inspires, since Pushkin, subversive
					thoughts: </p>
<p class="tx">
<i>I raised myself a monument not made by hands.</i> </p>
<p class="tx">
<i>Higher than Alexander’s column it proudly stands.</i> </p>
<p class="tx">
					That is, the inspired poet, like the icon painter, fulfills the work of the spirit:
					“a monument not made by hands.” An imitative classical column surmounted by a tsar,
					no matter how sumptuous, cannot really compete. </p>
<p class="tx">
					Within, as without, the very size of the Winter Palace weighs European forms against
					Russian vastness. Acres of marble stairway, tons of green-gleaming malachite from
					the Urals testify to the size of even the westernized Russian’s dreams and imagination.
					One could not, for a moment, confuse this structure with the Louvre, though its content
					certainly rivals that of the Parisian cynosure. Russian in size, it is also Russian-dingy,
					Russian-crowded, unventilated, and wretchedly lit. I recognize the Russian habit
					of ignoring most merely physical discomforts. Infected, though not, I think, terminally,
					with the contemporary craze for Impressionism, a friend and I abandoned <span id="z57-1">the group
					tour to specialize in Monet and Matisse. Three large second-floor halls harbor a
					fine collection of these artists’ works. Framed in glass, which reflected the lovely
					gray light of the square below, Waterloo Bridge, and even the more than famous water
					lilies of Giverny, were essentially invisible. Being Western, we cursed the conditions,
					despaired. Right then, however, a <i>babushka,</i> a museum guard in this case, set to watch
					over the Monets, took pity on us. Indeed, that seems to be the primary function of
					these omnipresent benign spirits. She rose from her knitting and seized us, in turn.
					Gravely pacing, she placed us on just that square of neglected oak parquet from which
					the foggy Thames, the gleaming lily-ponds, could be best admired. The execrable glass
					and its reflections disappeared. We were in Monet’s London, in provincial France,
					and then in paradise with Matisse’s dancers. When she found that we could, in a manner,
					speak Russian, this most admirable woman explained that the museum offers free seminars
					to those of its guards who wish to attend, that she had come to know at least the
					most prominent beauties of the collection and where to stand to view them. This work,
					which she loved, makes her tiny pension almost livable. For her, as ultimately for
					us, this Hermitage, wretchedly crowded and lit, is the temple of art that Catherine
					the Great envisaged. </span></p>
<p class="tx">
					In this most European structure, Russian impossibilities had been hardily and neatly
					surmounted, though not at all removed. My example will seem frivolous, but it is
					in just this manner, the investment of that patience and ingenuity which define love,
					that these people, these women in particular, contrive to endure. </p>
<p class="tx">
					Afterwards, in the Palace Square, it occurred to me that the perfect finale to such
					an experience would be a dish of Russian ice cream, which can rival the Italian,
					and a glass of Russian champagne. Once, in 1961, such delicacies had been available
					at the foot of Nevsky Prospect, the main street, and just two blocks from where we
					stood. Would the champagne and ice cream shop today, as is so often the case in Russia,
					be closed, under reconstruction, doing inventory, or simply dark? Depending now on
					serendipity, which is, strictly speaking, impossible, I led the way to the foot of
					Nevsky. The shop was open, uncrowded, and offered the desired delicacies. Our toasts,
					to the museum <i>babushka,</i> to Russia’s survival, to synchronicity, were heartfelt. </p>
<p class="tx">
					Our city tours here have tended, of course, to be largely aesthetic, given the presence
					of the Hermitage and the fine, though lesser known, Russian Museum of native works.
					Indeed, the city itself has a special, perhaps brutally aesthetic quality, because
					it was so obviously willed. Built by Peter’s order, and with slave labor, in a malarial
					swamp, on driven tree trunks, there are many human bones at its root. The layout
					is as geometric as possible given the numerous islands. Catherine the Great’s monument
					to Peter, Falconet’s “Bronze Horseman,” is the theoretical center of this geometry.
					That statue, which Pushkin took as the title of his most seminal work, embodies,
					as Pushkin suggests, the ruthless, and some think necessary, desire to succeed that
					is, for Russians, the Western element. The Neva flows before the “Horseman,” the
					lovely, granite-lined canals run concentrically behind it. St. Petersburg is, indeed,
					“a window hacked into Europe” for trade and for naval supremacy on the Baltic. However,
					like London, Hamburg, or any port <span id="z59-1">city, the sea wind brings freedom to breathe and
					to think that the hinterland doesn’t always know. </span></p>
<p class="tx">
					Our students loved the atmosphere of the “white nights” when, unable to sleep, we
					prowled the wide Nevsky Prospect, the Winter Palace Square, and admired the hugely
					delicate violets and sunflowers of the old cast iron fences: a city trademark. As
					Pushkin testified, one <i>can</i> read the newspaper at midnight without a lamp during that
					summer season. However, not being political sorts, we tended to complain at the absence
					of baseball scores rather than cheer for the opening of new hydro-electric stations.
					</p>
<p class="tx">
					Sometimes, on the Finnish Gulf, we built campfires on the beach, swam in the milky
					night waters, and talked. Once a young-old woman, and Russian life ages women quickly,
					remembered the nine hundred days of the German encirclement during World War II.
					Constant shelling, though the Finns, in their tepid alliance with the Germans, mostly
					fired high, aware that they would have to live with Russian neighbors after the conflict.
					The inhabitants knew bitter cold and wracking hunger, for it proved impossible to
					sufficiently supply the city across the ice of Lake Ladoga. The “Fascists” - the
					term always used by Russians for the German enemy of that time - never took the city.
					Our guest, who had been drinking, an unusual thing for Russian women, described the
					surreptitious burial of her starved children. She buried three, one after another,
					at night “so that they would not be eaten.” This left us, for once, quite silent.
					</p>
<p class="tx">
					On another occasion, one of the overseers of the camp praised her government pension,
					a commonplace under Soviet rule. Yes, she knew <span id="z60-1">of Stalin’s abuses and crimes. Who
					hadn’t lost relatives and friends in the purges? Still, she took what now seems,
					to me, a typically Russian view of the matter: <i>“They</i> have always overworked, starved,
					and murdered us. The miracle is that now so many have a crust of bread and can die
					<i>their own deaths.”</i> This is the Russian expression for dying in one’s own bed and
					of natural causes. “That’s luxury for you!” she added. Her words were utterly innocent
					of irony. Appleton, Wisconsin, seemed very far away. </span></p>
<p class="tx">
					In the environs of St. Petersburg, we usually visited Peterhof, the Founder’s summer
					palace on the Gulf, tsarskoe Selo, Catherine’s summer home and Pavlovsk, her son
					Paul’s country place. The heavily gilded fountains in the shapes of the gods and
					monsters of Greek myth, the ordered parks and ornate, rococo halls of all three,
					the acres of gold leaf, the classically geometric layouts, indicate how very Western
					this dynasty strove to be. The long grass insistently invading the formal grounds
					and the holes in the ceilings left by German and Russian target practice in WWII,
					suggested clearly that Western order does not maintain itself. </p>
<p class="tx">
					In any event, I soon reached my quota of such order and took refuge by rosy, lichen
					covered brick walls and battered gates where buskers, now deprived of their former
					state scholarships, raise well-worn French horns and cornets to celebrate Corelli
					and Gabrieli. When sighting American tourists, they even play “Home on the Range”
					and, not surprisingly, “Joe Hill,” the famous labor union song. The music somehow
					fits, glitters even, but warms in a way that gold leaf doesn’t. Still, I’m happy
					when we’re on the road to Novgorod. </p>
<p class="tx">
					This is oldest Russia, <i>Rus’</i> to initiates. The Novgorod Kremlin, for “kremlin” means
					simply “city stronghold” and was a feature of many old Russian towns, contains St.
					Sophia, the cathedral modeled upon that in Kiev. Both attempt to mirror the Orthodox
					mother church in Constantinople. Here also is the Great Bell, once used to call the
					city council or <i>veshch</i> into session, for Novgorod was the seat of a representative
					merchant polity before being conquered by Ivan the Terrible. The Great Bell is tongueless,
					martyred for having frightened a visiting tsar’s horse, or so the story goes. Novgorod
					had been a Hansa center, a trading post of that great international mercantile order
					of the Baltic, long after being founded by Viking merchants. </p>
<p class="tx">
					Sophia, the Church of the Holy Wisdom, has great bronze doors cast in Magdeburg with
					wide-mouthed lion door handles and Biblical scenes in which the bulging eyes of the
					figures are reminiscent of the chess figures carved of walrus ivory by those same
					Vikings. Russia was, indeed, before the Tatar yoke, variously and closely connected
					with northern Europe. Within the church, however, the gifts of Ivan the Terrible,
					a great silver chandelier, an ornate tsar’s box-throne where he, on visits, sat alone
					while all else stood in prayer, attest to Moscow’s ultimate dominance. </p>
<p class="tx">
					Above, in the limestone gallery, there is a telling collection of graffiti incised
					by other earlier worshippers in the twelfth century: </p>
<p class="tx">
					“May your hands wither, oh sinful soul.” </p>
<p class="tx">
					“Noblemen’s dishonor.” </p>
<p class="tx">
					“And I cry out: Oh my soul, why do you lie in luxury, why do you not rise up, why
					do you not adore your Lord? Why do you thirst for good, neglecting <span id="z62-1">to do good?” </span></p>
<p class="tx">
					Below these, and my favorite, is an odd little spell: “Pies in the oven, mushrooms
					in the dish, a quail rises in the oak grove. Serve the boiled groats, serve the pies.
					Go there.” I certainly will. </p>
<p class="tx">
					In the sanctuary below are incense and chanting and the mild glances of the icons.
					No, the St. Petersburg palaces of the eighteenth-century monarchs are not essential
					Russia. This view is fortified by at least a dozen small stone churches, each the
					gift of an ancient merchant family, which line the Volkhov River that splits Novgorod,
					some of them graced with the fiercer, hauntingly judgmental icons of Feofan Grek.
					Best of all is the outdoor museum of wooden architecture across from the town. Fir
					peasant huts, mills, barns, and churches were numbered and taken down in their native
					villages and brought here to celebrate the pine art of the north. Artisans - spinners,
					weavers and woodcarvers - can be seen at their trades, shabbier and more believable
					than at Williamsburg, impressive in the pride they bring to the task. The horse head
					ends of roof beams, incised suns and moons, attest to a pagan, Scandinavian ambience
					here. Intricate, lacy window frames display the ability of carpenters working largely
					with stone-honed hatchets rather than scroll saws. As no one now lives in these handsome
					structures, swallows nest in the many cracks and apertures and fill the air, the
					day, with purposeful, mosquito-fed twittering. Like these birds, the north Russians
					used the material of the region to build and survive. Unlike them, they spared some
					effort to include beauty shaved and hacked from close-grained spruce. There is hope
					in this. Humans need beauty. </p>
<p class="tx">
					The road from Novgorod to Moscow is little repaired though much used. Whoever was
					driving the van had to keep his eyes on the rutted macadam. The navigator clutched
					a rather too schematic map. (Complaining of this to the Intourist guide, I was answered:
					“If you don’t know where you’re going, you’d probably better not go there.”) The
					rest of us saw what seemed to be one village, or many villages run into one. In earlier
					times, goings and comings of tourists were very closely watched. Later, we simply
					got lost, and this could be quite enjoyable. Usually, however, we kept to the main
					road, admired the lovely and various window frames - not museum pieces only - and
					marveled at the small size of old and new log cabins. (In areas of historical preservation,
					if one does build, one is required to build a log cabin.) The homes are not much
					larger than single garages in the States. This does make them easier to heat with
					wood stoves, however. Here, too, on new structures, suns, stars, horse’s heads, and
					abstract curlicues encircle small, dark panes. </p>
<p class="tx">
					Our last stop before Moscow was Klin, Tchaikovsky’s hometown. The Tchaikovsky house/museum
					is a sprawling, gray clapboard structure with many decorated porches. “The 1812 Overture”
					resounds in the large, macadam parking lot. At this museum, Tchaikovsky’s music,
					the whole <i>oeuvre</i>, is played constantly during working hours. In a separate auditorium,
					one may choose a favorite work. We secured tickets, were assigned a guide, and accomplished
					a pious tour of rooms full of Victoriana: fringed lamps and tablecloths, oil portraits
					of the composer, his friends and family, and a grand piano in almost every room,
					usually a Bechstein. We walked the highly polished floors in felt over-slippers,
					as in all such museums, and deposited <span id="z64-1">them in a huge wooden crate, which was, at
					intervals, exchanged for its empty twin in the foyer. The guide’s patter resembled
					hagiography and was certainly not contemporary psychobiography. I find this rather
					pleasing and enjoy the strains of <i>Swan Lake</i>. Why <i>shouldn’t</i> the great artists be saints?
					Still, the best part of this visit, for me, was the large, grass-grown yard full
					of great old trees. Here, our own Intourist guide strolled and recited <i>Eugene Onegin</i>
					by heart, as in a play of Chekhov, and was charmingly melancholy. She was not unusual
					in her expertise. I have met naval engineers touring the States who knew Pushkin
					just as well. As Raya recited, her great pride in Russia’s culture was warmly evident.
					</span></p>
<p class="tx">
					I knew from earlier conversations that Raya accompanied western visitors who could
					only pity or sneer while viewing her country. The students, quite aware of this psychological
					electricity, were more than properly respectful, as when visiting a church in which
					one doesn’t know the rite. They didn’t even laugh when the needle stuck at “<i>1812</i>’s”
					cannons. I was proud of them. Somehow, they learned there is no need to ridicule
					the foreign, so we progressed thoughtfully toward Moscow with Napoleon’s cannon ringing
					forty-three times in our ears. </p>
<p class="tx">
					Our next stop <i>was</i> Moscow, and a fine old town it is, but I am no townsman. Summers,
					when I was not with the students in Eastern Europe, I spent those months cutting
					hay for my friend and neighbor Dennis. It is his opinion that a professor may be
					trusted to work on a farm if given a single chore, like cutting hay, and a few years,
					twelve in my case, to learn the ropes. I missed the summer alfalfa fields when in
					Russia, and kept an eye out for <span id="z65-1">agricultural matters to report to Dennis. Just then,
					though, what struck me was the particular soft orange-red of field poppies in young
					rye, found from Belgium to Russia and, of course, reminiscent of the blood shed in
					Europe’s great suicidal wars. Then there was the patient flow of sheep around the
					van when we met a flock in a narrow lane. They were weighty, cinnamon brown, and
					undulating like a woolly river. Other times we met the more threatening shoving of
					big, dark cream, horned cattle. Always following these herds was a thin, ragged herder
					with his scruffy, black-and-white, right hand, his dog. I imagined then his summer
					life as something a good deal nearer the real elements, rain and sun and hard stone,
					than our cushioned riding. Still, we shared the same long, rutted road, the three
					months of the same soft season. </span></p>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// chapter02.vue?93fa48e2